import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { assignableProfilesKeys } from "./useAssignableProfiles";

export interface AzureUser {
  azure_oid: string;
  email: string;
  display_name: string;
  first_name: string | null;
  last_name: string | null;
  azure_app_role?: string | null;
  azure_app_role_internal?: string | null;
}

interface SearchState {
  users: AzureUser[];
  isLoading: boolean;
  error: string | null;
}

interface ProvisionResult {
  user_id: string;
  is_provisioned: boolean;
  already_existed: boolean;
  role?: string;
  email_sent: boolean;
}

export type AssignmentContext = "defects" | "inspections";

/**
 * Hook for searching Azure AD users in real-time and provisioning them.
 * @param tenantId - The tenant to search within
 * @param context - Assignment context to filter results ('defects' or 'inspections')
 */
export function useAzureUserSearch(tenantId?: string, context?: AssignmentContext) {
  const [state, setState] = useState<SearchState>({
    users: [],
    isLoading: false,
    error: null,
  });
  const [isProvisioning, setIsProvisioning] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();

  /**
   * Search for Azure AD users matching the query.
   * Debouncing should be handled by the caller.
   */
  const searchUsers = useCallback(
    async (query: string): Promise<AzureUser[]> => {
      // Abort any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (!query || query.length < 3) {
        setState({ users: [], isLoading: false, error: null });
        return [];
      }

      abortControllerRef.current = new AbortController();

      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;

        if (!token) {
          throw new Error("Not authenticated");
        }

        const params = new URLSearchParams({ q: query });
        if (tenantId) {
          params.set("tenant_id", tenantId);
        }
        if (context) {
          params.set("context", context);
        }

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/search-azure-users?${params}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            signal: abortControllerRef.current.signal,
          },
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Search failed: ${response.status}`);
        }

        const data = await response.json();
        const users = data.users || [];

        setState({ users, isLoading: false, error: null });
        return users;
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          // Request was aborted, ignore
          return [];
        }

        const errorMessage = error instanceof Error ? error.message : "Search failed";
        setState({ users: [], isLoading: false, error: errorMessage });
        return [];
      }
    },
    [tenantId, context],
  );

  /**
   * Provision an Azure AD user so they can be assigned to tasks.
   * Returns the user_id that can be used for assignments.
   */
  const provisionUser = useCallback(
    async (
      user: AzureUser,
      targetTenantId: string,
      role?: string,
      personalMessage?: string | null,
    ): Promise<ProvisionResult> => {
      setIsProvisioning(true);

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;

        if (!token) {
          throw new Error("Not authenticated");
        }

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/provision-azure-user`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              azure_oid: user.azure_oid,
              email: user.email,
              display_name: user.display_name,
              first_name: user.first_name,
              last_name: user.last_name,
              tenant_id: targetTenantId,
              role,
              personal_message: personalMessage,
            }),
          },
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Provisioning failed: ${response.status}`);
        }

        const result: ProvisionResult = await response.json();

        // Invalidate assignable profiles cache so the new user appears
        queryClient.invalidateQueries({ queryKey: assignableProfilesKeys.all });

        // Clear search results since the user is now provisioned
        setState((prev) => ({
          ...prev,
          users: prev.users.filter((u) => u.azure_oid !== user.azure_oid),
        }));

        return result;
      } finally {
        setIsProvisioning(false);
      }
    },
    [queryClient],
  );

  /**
   * Clear search results.
   */
  const clearResults = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setState({ users: [], isLoading: false, error: null });
  }, []);

  return {
    azureUsers: state.users,
    isSearching: state.isLoading,
    searchError: state.error,
    isProvisioning,
    searchUsers,
    provisionUser,
    clearResults,
  };
}
